import { trackTelemetryEvent } from '@/lib/observability/telemetry';

export interface RuntimeMetricEvent {
  key: string;
  timestamp: number;
  metadata?: Record<string, string | number | boolean>;
}

export interface RuntimeMetricsSnapshot {
  counters: Record<string, number>;
  updatedAt: number;
  recentEvents: RuntimeMetricEvent[];
  resilienceHistory: RuntimeResilienceHistoryPoint[];
}

export interface RuntimeResilienceHistoryPoint {
  timestamp: number;
  resilienceScore: number;
  status: 'stable' | 'degraded' | 'critical';
}

export interface RuntimeResilienceSummary {
  windowMs: number;
  totalEvents: number;
  degradationEvents: number;
  recoveryEvents: number;
  resilienceScore: number;
  status: 'stable' | 'degraded' | 'critical';
  recommendations: string[];
}

export interface RuntimeResilienceAnomaly {
  id: string;
  severity: 'medium' | 'high' | 'critical';
  message: string;
  currentValue: number;
  baselineValue: number;
  delta: number;
}

export interface RuntimeDiagnosticsBundle {
  generatedAt: number;
  summary: RuntimeResilienceSummary;
  anomalies: RuntimeResilienceAnomaly[];
  snapshot: RuntimeMetricsSnapshot;
}

const STORAGE_KEY = 'intouch-runtime-metrics-v1';
const MAX_RECENT_EVENTS = 80;
const MAX_RESILIENCE_HISTORY = 336;

const EMPTY_SNAPSHOT: RuntimeMetricsSnapshot = {
  counters: {},
  updatedAt: 0,
  recentEvents: [],
  resilienceHistory: [],
};

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readSnapshot(): RuntimeMetricsSnapshot {
  if (!isBrowser()) return EMPTY_SNAPSHOT;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_SNAPSHOT;

    const parsed = JSON.parse(raw) as RuntimeMetricsSnapshot;
    if (!parsed || typeof parsed !== 'object') return EMPTY_SNAPSHOT;

    return {
      counters: typeof parsed.counters === 'object' && parsed.counters ? parsed.counters : {},
      updatedAt: typeof parsed.updatedAt === 'number' ? parsed.updatedAt : 0,
      recentEvents: Array.isArray(parsed.recentEvents) ? parsed.recentEvents.slice(0, MAX_RECENT_EVENTS) : [],
      resilienceHistory: Array.isArray((parsed as Partial<RuntimeMetricsSnapshot>).resilienceHistory)
        ? ((parsed as Partial<RuntimeMetricsSnapshot>).resilienceHistory as RuntimeResilienceHistoryPoint[]).slice(0, MAX_RESILIENCE_HISTORY)
        : [],
    };
  } catch {
    return EMPTY_SNAPSHOT;
  }
}

function writeSnapshot(snapshot: RuntimeMetricsSnapshot): void {
  if (!isBrowser()) return;

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    // Ignore persistence issues (private mode/quota exceeded).
  }
}

export function trackRuntimeMetric(
  key: string,
  amount: number = 1,
  metadata?: Record<string, string | number | boolean>
): void {
  if (!key || !Number.isFinite(amount)) return;

  const snapshot = readSnapshot();
  const nextValue = (snapshot.counters[key] || 0) + amount;

  const nextEvents: RuntimeMetricEvent[] = [
    {
      key,
      timestamp: Date.now(),
      metadata,
    },
    ...snapshot.recentEvents,
  ].slice(0, MAX_RECENT_EVENTS);

  writeSnapshot({
    counters: {
      ...snapshot.counters,
      [key]: nextValue,
    },
    updatedAt: Date.now(),
    recentEvents: nextEvents,
    resilienceHistory: snapshot.resilienceHistory,
  });

  // Mirror critical runtime reliability signals into centralized telemetry.
  if (
    key.startsWith('fx.') ||
    key.startsWith('exchange.') ||
    key.startsWith('staking.') ||
    key === 'chat.typing.permission_denied' ||
    key.startsWith('runtime.recovery.')
  ) {
    trackTelemetryEvent({
      name: `runtime.metric.${key}`,
      level: key.includes('error') || key.includes('failure') || key.includes('denied') ? 'warn' : 'info',
      metrics: {
        amount,
        total: nextValue,
      },
      context: metadata,
    });
  }
}

export function getRuntimeMetricsSnapshot(): RuntimeMetricsSnapshot {
  return readSnapshot();
}

export function clearRuntimeMetrics(): void {
  writeSnapshot(EMPTY_SNAPSHOT);
}

export function getRuntimeResilienceHistory(maxPoints: number = 48): RuntimeResilienceHistoryPoint[] {
  const snapshot = readSnapshot();
  const safeMax = Math.max(1, Math.min(MAX_RESILIENCE_HISTORY, Math.trunc(maxPoints)));
  return snapshot.resilienceHistory.slice(0, safeMax);
}

export function getRuntimeResilienceSummary(windowMs: number = 1000 * 60 * 60 * 24): RuntimeResilienceSummary {
  const snapshot = readSnapshot();
  const threshold = Date.now() - Math.max(0, windowMs);
  const events = snapshot.recentEvents.filter((event) => event.timestamp >= threshold);

  const degradationWeights: Record<string, number> = {
    'fx.provider.failure': 5,
    'fx.fallback.used': 7,
    'exchange.price_feed.demo_mode': 10,
    'exchange.price_feed.fetch_error': 8,
    'exchange.price_feed.timeout': 6,
    'chat.typing.permission_denied': 4,
    'staking.fetch.error': 9,
    'staking.read.fallback_exhausted': 6,
  };

  const recoveryWeights: Record<string, number> = {
    'fx.provider.success': 1,
    'fx.cache.hit': 0.5,
    'exchange.price_feed.live_success': 1,
    'staking.read.fallback_recovered': 1,
  };

  let degradationEvents = 0;
  let recoveryEvents = 0;
  let penalty = 0;
  let recoveryCredit = 0;

  for (const event of events) {
    if (degradationWeights[event.key]) {
      degradationEvents += 1;
      penalty += degradationWeights[event.key];
    }

    if (recoveryWeights[event.key]) {
      recoveryEvents += 1;
      recoveryCredit += recoveryWeights[event.key];
    }
  }

  const resilienceScore = Math.max(0, Math.min(100, Math.round(100 - penalty + recoveryCredit)));
  const status: RuntimeResilienceSummary['status'] =
    resilienceScore >= 80 ? 'stable' : resilienceScore >= 55 ? 'degraded' : 'critical';

  const recommendations: string[] = [];

  const fxFallbackCount = events.filter((event) => event.key === 'fx.fallback.used').length;
  if (fxFallbackCount >= 3) {
    recommendations.push('Frequent FX fallback detected. Clear FX cache and verify provider reachability.');
  }

  const demoModeCount = events.filter((event) => event.key === 'exchange.price_feed.demo_mode').length;
  if (demoModeCount >= 2) {
    recommendations.push('Exchange demo mode triggered repeatedly. Review market API connectivity and rate limits.');
  }

  const stakingErrorCount = events.filter((event) => event.key === 'staking.fetch.error').length;
  if (stakingErrorCount >= 2) {
    recommendations.push('Staking fetch errors are recurring. Check RPC health and contract method compatibility.');
  }

  const typingDeniedCount = events.filter((event) => event.key === 'chat.typing.permission_denied').length;
  if (typingDeniedCount >= 3) {
    recommendations.push('Typing permission denials are elevated. Revalidate RTDB typing rules and conversation keys.');
  }

  if (recommendations.length === 0) {
    recommendations.push('Runtime resilience is healthy. Continue monitoring for anomaly spikes.');
  }

  return {
    windowMs,
    totalEvents: events.length,
    degradationEvents,
    recoveryEvents,
    resilienceScore,
    status,
    recommendations,
  };
}

export function appendRuntimeResilienceHistoryPoint(options?: {
  windowMs?: number;
  minIntervalMs?: number;
}): RuntimeResilienceHistoryPoint | null {
  const windowMs = options?.windowMs ?? 1000 * 60 * 60 * 24;
  const minIntervalMs = options?.minIntervalMs ?? 1000 * 60 * 10;
  const now = Date.now();

  const snapshot = readSnapshot();
  const last = snapshot.resilienceHistory[0];
  if (last && now - last.timestamp < minIntervalMs) {
    return null;
  }

  const summary = getRuntimeResilienceSummary(windowMs);
  const point: RuntimeResilienceHistoryPoint = {
    timestamp: now,
    resilienceScore: summary.resilienceScore,
    status: summary.status,
  };

  writeSnapshot({
    ...snapshot,
    updatedAt: now,
    resilienceHistory: [point, ...snapshot.resilienceHistory].slice(0, MAX_RESILIENCE_HISTORY),
  });

  return point;
}

export function getRuntimeResilienceAnomalies(options?: {
  historyWindow?: number;
  comparisonWindow?: number;
}): RuntimeResilienceAnomaly[] {
  const historyWindow = Math.max(4, options?.historyWindow ?? 12);
  const comparisonWindow = Math.max(2, options?.comparisonWindow ?? 4);
  const history = getRuntimeResilienceHistory(historyWindow);

  if (history.length < comparisonWindow * 2) {
    return [];
  }

  const recent = history.slice(0, comparisonWindow);
  const baseline = history.slice(comparisonWindow, comparisonWindow * 2);

  const recentAvg = recent.reduce((sum, point) => sum + point.resilienceScore, 0) / recent.length;
  const baselineAvg = baseline.reduce((sum, point) => sum + point.resilienceScore, 0) / baseline.length;
  const avgDelta = Math.round(recentAvg - baselineAvg);

  const anomalies: RuntimeResilienceAnomaly[] = [];

  if (avgDelta <= -20) {
    anomalies.push({
      id: 'score_drop_critical',
      severity: 'critical',
      message: 'Resilience score dropped sharply versus recent baseline.',
      currentValue: Math.round(recentAvg),
      baselineValue: Math.round(baselineAvg),
      delta: avgDelta,
    });
  } else if (avgDelta <= -10) {
    anomalies.push({
      id: 'score_drop_high',
      severity: 'high',
      message: 'Resilience score is trending down versus baseline.',
      currentValue: Math.round(recentAvg),
      baselineValue: Math.round(baselineAvg),
      delta: avgDelta,
    });
  }

  const criticalCount = recent.filter((point) => point.status === 'critical').length;
  if (criticalCount >= Math.ceil(recent.length / 2)) {
    anomalies.push({
      id: 'critical_status_cluster',
      severity: 'critical',
      message: 'Critical resilience status appears repeatedly in recent samples.',
      currentValue: criticalCount,
      baselineValue: 0,
      delta: criticalCount,
    });
  }

  const stableCount = recent.filter((point) => point.status === 'stable').length;
  if (stableCount === 0 && anomalies.length === 0) {
    anomalies.push({
      id: 'stable_absence',
      severity: 'medium',
      message: 'No stable resilience samples in the recent window.',
      currentValue: 0,
      baselineValue: 1,
      delta: -1,
    });
  }

  return anomalies;
}

export function getRuntimeDiagnosticsBundle(): RuntimeDiagnosticsBundle {
  const snapshot = getRuntimeMetricsSnapshot();
  const summary = getRuntimeResilienceSummary();
  const anomalies = getRuntimeResilienceAnomalies();

  return {
    generatedAt: Date.now(),
    summary,
    anomalies,
    snapshot,
  };
}
